import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { FiSave, FiX } from 'react-icons/fi';
import { repairService } from '../../services/repairService';
import ImageUploader from '../../components/Common/ImageUploader';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { STATIC_BASE_URL, getImageUrl } from '../../utils/constants'; // Changed this line

const RepairForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  // Fetch article if editing
  const { data: article, isLoading } = useQuery(
    ['repair', id],
    () => repairService.getById(id),
    { enabled: !!id }
  );

  useEffect(() => {
    if (article) {
      setValue('title', article.title);
      setValue('description', article.description);
      setValue('author', article.author);
      setExistingImages(article.images || []);
    }
  }, [article, setValue]);

  const createMutation = useMutation(
    (formData) => repairService.create(formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('repairs');
        toast.success('បង្កើតអត្ថបទដោយជោគជ័យ');
        navigate('/admin/repairs');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចបង្កើតអត្ថបទបានទេ');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, formData }) => repairService.update(id, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('repairs');
        toast.success('កែប្រែអត្ថបទដោយជោគជ័យ');
        navigate('/admin/repairs');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចកែប្រែអត្ថបទបានទេ');
      },
    }
  );

  const deleteImageMutation = useMutation(
    ({ repairId, imageId }) => repairService.deleteImage(repairId, imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['repair', id]);
        toast.success('លុបរូបភាពដោយជោគជ័យ');
      },
      onError: (error) => {
        toast.error('មិនអាចលុបរូបភាពបានទេ');
      },
    }
  );

  const onSubmit = async (data) => {
    setUploading(true);
    const formData = new FormData();
    
    // Append form fields
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });

    // Append new images
    images.forEach(image => {
      formData.append('images', image);
    });

    try {
      if (id) {
        await updateMutation.mutateAsync({ id, formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = (newImages) => {
    setImages([...images, ...newImages]);
  };

  const handleImageRemove = (imageToRemove) => {
    if (imageToRemove.id) {
      // Existing image
      deleteImageMutation.mutate({
        repairId: id,
        imageId: imageToRemove.id
      });
      setExistingImages(existingImages.filter(img => img.id !== imageToRemove.id));
    } else {
      // New image
      setImages(images.filter(img => img !== imageToRemove));
    }
  };

  if (id && isLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'កែប្រែអត្ថបទជួសជុល' : 'បន្ថែមអត្ថបទ'}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">ព័ត៌មានអត្ថបទ</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ចំណងជើង *
              </label>
              <input
                {...register('title', { 
                  required: 'សូមបញ្ចូលចំណងជើង',
                  minLength: { value: 5, message: 'ចំណងជើងត្រូវតែមានយ៉ាងតិច ៥ តួអក្សរ' }
                })}
                className={`w-full px-4 py-2 border border-gray-200 focus:outline-none focus:border-red-600 ${
                  errors.title ? 'border-red-500' : 'bg-gray-50'
                }`}
                placeholder="បញ្ចូលចំណងជើងអត្ថបទ"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                អ្នកនិពន្ធ
              </label>
              <input
                {...register('author')}
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 focus:outline-none focus:border-red-600"
                placeholder="បញ្ចូលឈ្មោះអ្នកនិពន្ធ (ស្រេចចិត្ត)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ព័ត៌មានលម្អិត *
              </label>
              <textarea
                {...register('description', { required: 'សូមបញ្ចូលព័ត៌មានលម្អិត' })}
                rows="10"
                className={`w-full px-4 py-2 border border-gray-200 font-mono focus:outline-none focus:border-red-600 ${
                  errors.description ? 'border-red-500' : 'bg-gray-50'
                }`}
                placeholder="បញ្ចូលព័ត៌មានលម្អិតអត្ថបទ (HTML tags អាចប្រើបាន)"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                HTML tags អាចប្រើបាន។ ប្រើ &lt;pre&gt; សម្រាប់ទ្រង់ទ្រាយអក្សរ។
              </p>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">រូបភាពអត្ថបទ</h2>
          
          <ImageUploader
            images={[
              ...existingImages.map(img => ({
                id: img.id,
                url: getImageUrl(img.filepath, 'repairs'),
                filepath: img.filepath
              })),
              ...images
            ]}
            onUpload={handleImageUpload}
            onRemove={handleImageRemove}
            maxFiles={10}
          />
          <p className="mt-2 text-sm text-gray-500">
            អាចផ្ទុករូបភាពបានរហូតដល់ 10 សន្លឹក។ ទ្រង់ទ្រាយដែលអាចប្រើបាន៖ JPG, PNG, GIF, WEBP (ទំហំអតិបរមា 5MB ក្នុងមួយសន្លឹក)
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/repairs')}
            className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
          >
            <FiX className="mr-2" /> បោះបង់
          </button>
          <button
            type="submit"
            disabled={uploading}
            className="bg-red-600 text-white px-4 py-2 flex items-center hover:bg-red-700 transition-colors disabled:bg-gray-400"
          >
            <FiSave className="mr-2" />
            {uploading ? 'កំពុងរក្សាទុក...' : 'រក្សាទុកអត្ថបទ'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RepairForm;